import { useIntl } from 'react-intl';
import SupportRating from './support-rating';
import { FreshdeskFeedbackTicket } from 'library/types';
import { RATING_VALUES, RatingLabels, RatingValue } from '../constants';
import { Alert, AlertTitle, Box, Divider, Paper, Stack, Typography } from '@mui/material';

type AnsweredFeedbackProps = {
  ticket: FreshdeskFeedbackTicket;
  ratingLabels: RatingLabels;
};

const AnsweredFeedback = ({ ticket, ratingLabels }: AnsweredFeedbackProps) => {
  const intl = useIntl();
  const rating = RATING_VALUES.includes(ticket.rate as RatingValue) ? (ticket.rate as RatingValue) : 0;
  const description = typeof ticket.description === 'string' ? ticket.description.trim() : '';

  return (
    <Stack spacing={2.5}>
      <Alert
        role="status"
        aria-live="polite"
        severity="success"
        variant="outlined"
        sx={{ alignItems: 'flex-start', borderRadius: 2 }}>
        <AlertTitle sx={{ mb: 0.5, fontWeight: 700 }}>
          {intl.formatMessage({
            id: 'ticketSurvey.answeredNoticeTitle',
            defaultMessage: 'Bu anket cevaplanmıştır',
          })}
        </AlertTitle>
        {intl.formatMessage({
          id: 'ticketSurvey.answeredNoticeMessage',
          defaultMessage: 'Paylaştığınız puan ve açıklama yalnızca görüntülenebilir, değiştirilemez.',
        })}
      </Alert>

      <Paper
        component="section"
        variant="outlined"
        aria-label={intl.formatMessage({
          id: 'ticketSurvey.answeredSummaryAria',
          defaultMessage: 'Gönderilmiş değerlendirme özeti',
        })}
        sx={(theme) => ({
          p: { xs: 2, sm: 2.5 },
          borderRadius: 2.5,
          backgroundColor: theme.palette.action.hover,
        })}>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
              {intl.formatMessage({
                id: 'ticketSurvey.submittedRating',
                defaultMessage: 'Verdiğiniz puan',
              })}
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 1, sm: 2 }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <SupportRating
                readOnly
                value={rating}
                getLabelText={(value) => `${value} / 5 - ${ratingLabels[value as RatingValue]}`}
                emptyLabelText={intl.formatMessage({
                  id: 'ticketSurvey.ratingUnavailable',
                  defaultMessage: 'Puan bilgisi bulunamadı',
                })}
              />
              <Typography variant="body2" fontWeight={700} color="text.secondary">
                {rating
                  ? `${rating} / 5 — ${ratingLabels[rating]}`
                  : intl.formatMessage({
                      id: 'ticketSurvey.ratingUnavailable',
                      defaultMessage: 'Puan bilgisi bulunamadı',
                    })}
              </Typography>
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {intl.formatMessage({
                id: 'ticketSurvey.submittedDescription',
                defaultMessage: 'Paylaştığınız açıklama',
              })}
            </Typography>
            <Typography
              component="p"
              variant="body2"
              color={description ? 'text.primary' : 'text.secondary'}
              sx={{ m: 0, lineHeight: 1.7, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {description ||
                intl.formatMessage({
                  id: 'ticketSurvey.noDescription',
                  defaultMessage: 'Bu değerlendirmede açıklama paylaşılmamış.',
                })}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default AnsweredFeedback;
